import CustomSnackBar from "@components/CustomSnackBar";
import { ROUTING_ADMIN_CLIENT_MODELS } from "@constants/adminPanelRouting";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import ROUTES from "@constants/routing";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ScienceIcon from "@mui/icons-material/Science";
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Skeleton,
  Typography
} from "@mui/material";
import { useClient, useSaveClientModel } from "@services/api";
import { useQueryClient } from "@tanstack/react-query";
import { getTranslationKeyFromAPIError } from "@utils/api_errors";
import ClientModelForm, {
  ClientModelCreateForm,
  ClientModelFormSkeleton
} from "@views/ViewAdminPanel/components/ClientModelForm";
import NavigationButtons from "@views/ViewAdminPanel/components/NavigationButtons";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

type M_PROCESSING = "loading" | "draft" | "saving" | "created" | "failed";

const ViewNewClientModel = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id: clientId } = useParams<{ id: string }>();

  const [processing, setProcessing] = useState(false);

  const [model, setModel] = useState<ClientModelCreateForm>({
    name: "",
    year: 0,
    aggregation_type: "",
    input_file: null,
    output_file: null,
    parameters_file: null,
    baseline_organic_file: null
  });
  const [modelStatus, setModelStatus] = useState<M_PROCESSING>("loading");
  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });

  const mutationSaveClientModel = useSaveClientModel();
  const queryClient = useQueryClient();

  const {
    data: client,
    isFetching: isLoadingClient,
    isError
  } = useClient(clientId || "");

  const onGoBack = () => {
    navigate(`${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_CLIENT_MODELS}`, {
      state: { clientId }
    });
  };

  const onContinue = () => {
    setModelStatus("draft");
    navigate(`${ROUTES.ADMIN_PANEL}${ROUTING_ADMIN_CLIENT_MODELS}`, {
      state: { clientId }
    });
  };

  const onSave = (e: any, model: ClientModelCreateForm) => {
    e.preventDefault();
    if (
      !e.target.checkValidity() ||
      model.name.length === 0 ||
      model.year === 0 ||
      model.aggregation_type.length === 0 ||
      model.input_file === null ||
      model.output_file === null ||
      model.parameters_file === null
    ) {
      setSnackbarAlert(setErrorAlert(t("newClientModel.formInvalid")));
      return;
    } else {
      setProcessing(true);
      setModel({ ...model });
      setModelStatus("saving");
      mutationSaveClientModel.mutate(
        {
          client_id: clientId || "",
          name: model.name,
          year: model.year,
          aggregation_type: model.aggregation_type,
          input_file: model.input_file,
          output_file: model.output_file,
          parameters_file: model.parameters_file,
          baseline_organic_file: model.baseline_organic_file
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["client", clientId, "models"]
            });
            queryClient.refetchQueries({
              queryKey: ["client", clientId, "models"]
            });
            setProcessing(false);
            setModelStatus("created");
          },
          onError: (error: any) => {
            setProcessing(false);
            setModelStatus("draft");
            const translationKey = getTranslationKeyFromAPIError(
              "newClientModel.errors",
              error
            );
            const messageKey = i18n.exists(translationKey)
              ? translationKey
              : "newClientModel.creation_error";
            setSnackbarAlert(
              setErrorAlert(
                t(messageKey, {
                  name: model.name,
                  kpi: client?.kpi_label.toLocaleLowerCase() || ""
                })
              )
            );
          }
        }
      );
    }
  };

  const clientName = useMemo(() => client?.name || "", [client]);

  useEffect(() => {
    if (!isLoadingClient && !isError) {
      setModelStatus("draft");
    }
  }, [isLoadingClient, isError]);

  return (
    <Box className="w-full p-4">
      <NavigationButtons disabled={processing} onGoBack={onGoBack} />
      <Box className="flex justify-center w-full p-2 md:p-8 mt-8">
        <Paper className="w-full">
          <Box className="flex items-center justify-center pt-16 gap-2">
            <Box className="flex">
              <ScienceIcon color="success" sx={{ fontSize: "2.0rem" }} />
              <sup>
                <AddIcon color="success" sx={{ fontSize: "1.5rem" }} />
              </sup>
            </Box>
            {!isLoadingClient && clientName.length > 0 ? (
              <Typography variant="h6">
                {clientName}: {t("common.newModel")}
              </Typography>
            ) : (
              <Skeleton variant="text" width="10%" height="100%" />
            )}
          </Box>
          {modelStatus === "loading" && <ClientModelFormSkeleton />}
          {modelStatus === "draft" && (
            <ClientModelForm
              initialModel={model}
              onSave={onSave}
              onGoBack={onGoBack}
              processing={processing}
            />
          )}
          {modelStatus === "saving" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("createClientProgressDialog.status.clientModelSaving", {
                  name: model.name
                })}
              </Typography>
              <LinearProgress color="success" sx={{ height: "1.0rem" }} />
            </Box>
          )}
          {modelStatus === "created" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="success" className="pb-4">
                {t("newClientModel.creation_success", {
                  name: model.name
                })}
              </Typography>
              <Button color="success" variant="contained" onClick={onContinue}>
                {t("common.continue")}
                <ArrowForwardIosIcon sx={{ fontSize: "1.5rem" }} />
              </Button>
            </Box>
          )}
          {modelStatus === "failed" && (
            <Box className="w-full p-8">
              <Typography variant="h5" color="error" className="pb-4">
                {t("newClientModel.creation_error", {
                  name: model.name
                })}
              </Typography>
            </Box>
          )}
        </Paper>
        <CustomSnackBar
          id={snackbarAlert.message}
          key={snackbarAlert.message}
          open={snackbarAlert.open}
          message={snackbarAlert.message}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          autoHideDuration={snackbarAlert.autoHideDuration}
          color={snackbarAlert.severity}
        />
      </Box>
    </Box>
  );
};

export default ViewNewClientModel;
